import { css } from '@emotion/react';
import { useDevToolsStore } from '@frontend/devtools';
import { useDashboardWidget } from '@frontend/grid-dashboard';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Chip, ContentLoader, NakedButton, useModalControl, useTooltip } from '@frontend/design-system';
import { useSchedulingLocationInfo } from '../../../../../../../hooks/use-scheduling-location-info';
import { useScheduleRequestActions } from '../../hooks/use-schedule-request-actions';
import { ScheduleRequestsTrackingIds } from '../../trackingIds';
import { ScheduleRequestDeleteConfirmationModal } from '../components/schedule-request-delete-confirmation-modal';
import { ScheduleRequestDismissConfirmationModal } from '../components/schedule-request-dismiss-confirmation-modal';
import { ScheduleRequestRejectConfirmationModal } from '../components/schedule-request-reject-confirmation-modal';
import { ScheduleRequestItemHeader } from '../schedule-request-item-header';
import type { SchedulePulseRequestListItemProps } from '../SchedulePulseRequestList/SchedulePulseRequestListItem';

export const ScheduleRequestWidgetListItem = (props: SchedulePulseRequestListItemProps) => {
  const { listItem } = props;
  const isDebugModeOn = useDevToolsStore((state) => state.options.isDebugModeOn);
  const debugStyle =
    isDebugModeOn && listItem.linkHash === 'v2' ? { backgroundColor: theme.colors.critical10 } : undefined;
  const { currentSize } = useDashboardWidget();
  const isNarrowSize = currentSize === 'large-narrow';
  const { tooltipProps, triggerProps, Tooltip } = useTooltip({ trigger: 'hover' });
  const { isMultiLocation } = useSchedulingLocationInfo();

  const isPatientContacted = listItem.status === 'MESSAGED' || listItem.status === 'CALLED';
  const patientContactedAt = isPatientContacted ? listItem.reviewedAt : undefined;

  const deleteRequestModalProps = useModalControl();
  const dismissRequestModalProps = useModalControl();
  const rejectRequestModalProps = useModalControl();

  const {
    isLoading,
    handleMessage,
    handlePhoneCall,
    handleCompleteRequest,
    handleDeleteRequest,
    handleDismissRequest,
    handleRejectRequest,
  } = useScheduleRequestActions(listItem);

  return (
    <div
      css={[containerStyle, debugStyle]}
      data-trackingId={ScheduleRequestsTrackingIds.requestListItemAccordion}
      style={{ borderBottom: `1px solid ${theme.colors.neutral10}` }}
      key={listItem?.id ?? ''}
    >
      <ContentLoader show={isLoading} size='small' />
      <ScheduleRequestItemHeader
        {...props}
        patientContactedAt={patientContactedAt}
        deleteRequestModalProps={deleteRequestModalProps}
        dismissRequestModalProps={dismissRequestModalProps}
        rejectRequestModalProps={rejectRequestModalProps}
        onCompleteRequest={handleCompleteRequest}
        onPhoneCall={handlePhoneCall}
        onMessage={handleMessage}
        isWidgetListItem
        locationChip={(locationName) =>
          isMultiLocation && (
            <>
              {isNarrowSize ? (
                <NakedButton {...triggerProps} css={locationIconButtonStyle}>
                  <Icon name='location' size={16} />
                  <Tooltip {...tooltipProps}>{locationName}</Tooltip>
                </NakedButton>
              ) : (
                <Chip.Location variant='neutral' tooltipLabel={locationName}>
                  {locationName}
                </Chip.Location>
              )}
            </>
          )
        }
      />
      <ScheduleRequestDeleteConfirmationModal onConfirm={handleDeleteRequest} {...deleteRequestModalProps.modalProps} />
      <ScheduleRequestRejectConfirmationModal onConfirm={handleRejectRequest} {...rejectRequestModalProps.modalProps} />
      <ScheduleRequestDismissConfirmationModal
        onConfirm={handleDismissRequest}
        {...dismissRequestModalProps.modalProps}
      />
    </div>
  );
};

const containerStyle = css({
  borderRadius: theme.borderRadius.small,
  backgroundColor: theme.colors.white,
  padding: theme.spacing(0.5, 2),
  marginBottom: theme.spacing(1),
  boxShadow: theme.shadows.light,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const locationIconButtonStyle = css`
  margin-left: auto;
`;
